import { convertiUtsToDate } from '../../GrapheXT/GrapheXT';
import { EVENT_STYLES, DEFAULT_EVENT_STYLE } from './labelConstants';
import { IA_CATEGORIES, detectCategory } from './iaCategories';

export const buildAnnotation = ({
  xDate,
  paperY,
  label,
  dateStr,
  note,
  color,
  valLine = '',
}) => ({
  x: xDate,
  xref: 'x',
  y: paperY,
  yref: 'paper',
  text:
    `<b style="font-size:13px">${label}</b>` +
    `<br><span style="color:#555">Date :</span>  ${dateStr}` +
    valLine +
    (note ? `<br><span style="color:#555">Note :</span>  ${note}` : ''),
  showarrow: true,
  arrowhead: 2,
  arrowsize: 0.8,
  arrowwidth: 2,
  arrowcolor: color,
  ax: 0,
  ay: 36,
  xanchor: 'center',
  yanchor: 'top',
  cliponaxis: false,
  align: 'left',
  font: { size: 12, color: '#1a1a2e', family: 'Montserrat, sans-serif' },
  bgcolor: 'rgba(255,255,255,0.97)',
  bordercolor: color,
  borderwidth: 1.5,
  borderpad: 10,
});

export const buildEventTraces = ({
  items,
  tRaw,
  yRaw,
  donnees,
  yMax,
  domain,
  yaxisKey,
  markerSize,
  shownLabels,
  graphCurves,
}) => {
  const sorted = [...items]
    .map((ev, i) => ({ ...ev, _i: i }))
    .sort((a, b) => a.t - b.t);

  // Group-based stacking variables removed — all labels render at the same height (0.5)
  const traces = [];
  const annMeta = [];
  const images = [];
  const categories = [];
  // Two invisible anchor points spanning the full x range — prevents Plotly from
  // dimming dummy legend traces when hovermode:'x' is active.
  const xFirst = tRaw.length > 0 ? convertiUtsToDate([tRaw[0]])[0] : null;
  const xLast  = tRaw.length > 1 ? convertiUtsToDate([tRaw[tRaw.length - 1]])[0] : xFirst;

  sorted.forEach((ev, i) => {
    const iconType = ev.iconType || ev.label;
    const style = EVENT_STYLES[iconType] || DEFAULT_EVENT_STYLE;
    const xDate = convertiUtsToDate([ev.t])[0];
    const markerY = 0.5;

    // Recherche la courbe correspondant au capteur/position du label.
    let activeCurve = null;
    if (donnees?.length && ev.idSensor != null) {
      const sid = Number(ev.idSensor);
      const pos = Number(ev.positionInSensor ?? -1);
      activeCurve =
        donnees.find((d) => {
          const dPos = Number(
            d.position_in_sensor ?? d.channel ?? d.position ?? -1
          );
          return Number(d.id_sensor) === sid && dPos === pos;
        }) ?? null;
      if (!activeCurve && graphCurves?.length) {
        const gcIdx = graphCurves.findIndex((gc) => {
          const nums = (gc.formula || '').match(/\d+/g)?.map(Number);
          return nums?.length >= 2 && nums[0] === sid && nums[1] === pos;
        });
        if (gcIdx >= 0 && gcIdx < donnees.length) activeCurve = donnees[gcIdx];
      }
      if (!activeCurve) {
        const bySensor = donnees.filter((d) => Number(d.id_sensor) === sid);
        if (bySensor.length === 1) activeCurve = bySensor[0];
      }
    }
    const rawT = activeCurve ? activeCurve.x || activeCurve.t || tRaw : tRaw;
    const activeT = rawT.length && typeof rawT[0] === 'number' ? rawT : tRaw;
    const activeY = activeCurve ? activeCurve.y || [] : yRaw;

    let yCurve = yMax;
    if (activeT.length > 0) {
      let closest = 0,
        minDiff = Math.abs(activeT[0] - ev.t);
      for (let j = 1; j < activeT.length; j++) {
        const diff = Math.abs(activeT[j] - ev.t);
        if (diff < minDiff) {
          minDiff = diff;
          closest = j;
        }
      }
      yCurve = activeY[closest] ?? yMax;
    }

    const rawLabel = (
      ev.label_text ??
      ev.label ??
      iconType ??
      'Sans label'
    ).trim();
    const displayLabel =
      rawLabel.length > 20 ? `${rawLabel.slice(0, 20)}...` : rawLabel;

    const isJalon  = iconType === 'diamond';
    const isCircle = iconType === 'circle';

    // Label MANUAL ou IA cercle dont le texte correspond à une catégorie → emoji à la place du marker
    const detectedCat    = (isJalon || isCircle) ? detectCategory(rawLabel) : null;
    const detectedEntry  = detectedCat ? IA_CATEGORIES[detectedCat] : null;
    // Jalon sans catégorie → cercle bleu standard ; avec catégorie → couleur de la catégorie
    const effectiveStyle = detectedEntry
      ? { color: detectedEntry.color, symbol: 'circle' }
      : isJalon
        ? { color: EVENT_STYLES.circle.color, symbol: 'circle' }
        : style;
    const effectiveType  = detectedCat ?? iconType;
    const categoryLabel  =
      effectiveType === 'diamond' ? 'Jalon'
      : effectiveType === 'circle' ? 'IA'
      : effectiveType;
    const isPlotlyMarker = (isJalon || isCircle) && !detectedCat;

    const legendGroup = `category:${effectiveType}`;
    const showLegend =
      !shownLabels.has(legendGroup) &&
      (() => {
        shownLabels.add(legendGroup);
        return true;
      })();

    // Per-text group: labels with the same text share one legend entry.
    const plotlyMarkerGroup = `label:${rawLabel}`;
    const showPlotlyLegend =
      !shownLabels.has(plotlyMarkerGroup) &&
      (() => {
        shownLabels.add(plotlyMarkerGroup);
        return true;
      })();

    if (isPlotlyMarker) {
      traces.push({
        type: 'scatter',
        mode: 'markers',
        name: displayLabel,
        legendgroup: plotlyMarkerGroup,
        showlegend: showPlotlyLegend,
        xaxis: 'x',
        yaxis: yaxisKey,
        x: [xDate],
        y: [markerY],
        cliponaxis: false,
        hoverinfo: 'none',
        customdata: [{ type: 'label', t: ev.t, annMetaIdx: i, itemIdx: ev._i }],
        marker: {
          size: markerSize,
          color: effectiveStyle.color,
          symbol: 'circle',
          line: { color: effectiveStyle.color, width: 2 },
        },
      });
    } else {
      // Catégorie IA nommée OU label MANUAL avec catégorie détectée — rendu emoji
      const emoji = IA_CATEGORIES[effectiveType]?.emoji ?? effectiveType.charAt(0);
      traces.push({
        type: 'scatter',
        mode: 'text',
        name: '',
        showlegend: false,
        legendgroup: legendGroup,
        x: [xDate],
        y: [markerY],
        yaxis: yaxisKey,
        xaxis: 'x',
        text: [emoji],
        textfont: { size: markerSize * 2, color: effectiveStyle.color },
        textposition: 'middle center',
        cliponaxis: false,
        hoverinfo: 'none',
        customdata: [{ type: 'label', t: ev.t, annMetaIdx: i, itemIdx: ev._i }],
      });
      if (showLegend) {
        const legendEmoji = IA_CATEGORIES[effectiveType]?.emoji ?? '';
        const legendName = legendEmoji
          ? `${legendEmoji} <span style="color:${effectiveStyle.color}">${categoryLabel}</span>`
          : `<span style="color:${effectiveStyle.color}">${categoryLabel}</span>`;
        traces.push({
          type: 'scatter',
          mode: 'lines+markers',
          name: legendName,
          legendgroup: legendGroup,
          showlegend: true,
          visible: true,
          x: [xFirst, xLast],
          y: [-100, -100],
          xaxis: 'x',
          yaxis: yaxisKey,
          hoverinfo: 'none',
          line: { color: effectiveStyle.color, width: 2 },
          marker: { color: effectiveStyle.color, size: 6, symbol: 'circle' },
        });
        categories.push({ iconType: effectiveType, color: effectiveStyle.color, name: categoryLabel });
      }
    }

    const dateStr = new Date(ev.t * 1000).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const valLine = `<br><span style="color:#555">Valeur :</span>  <b>${Math.round(yCurve * 10) / 10}</b>`;

    annMeta.push({
      xDate,
      t: ev.t,
      markerY,
      style: effectiveStyle,
      legendGroup: isPlotlyMarker ? plotlyMarkerGroup : legendGroup,
      label: displayLabel,
      note: ev.note,
      dateStr,
      yCurveVal: yCurve,
      valLine,
      domain,
      itemIdx: ev._i,
    });
  });

  return { traces, annMeta, images, categories };
};
